import Layout from '../../components/feature/Layout';
import { useReveal } from '../../hooks/useReveal';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { monthlyTradeVolume, tradeCategories, tradeDistribution } from '../../mocks/tradeData';

export default function TradeDataPage() {
  const ref = useReveal();
  const { t } = useTranslation();

  const tradeStats = [
    { labelKey: 'tradeDataPage.stat.totalVolume', value: '$4.2B', change: '+18.3%', up: true },
    { labelKey: 'tradeDataPage.stat.activeCompanies', value: '1,240+', change: '+12.1%', up: true },
    { labelKey: 'tradeDataPage.stat.shipments', value: '38,500', change: '+22.7%', up: true },
    { labelKey: 'tradeDataPage.stat.nations', value: '24', change: '+4', up: true },
  ];

  return (
    <Layout>
      <title>{t('page.title.tradeData')}</title>
      <meta name="robots" content="noindex, nofollow" />
      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-24 bg-navy overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <svg className="w-full h-full" viewBox="0 0 1440 400">
            {Array.from({ length: 10 }).map((_, i) => (
              <line key={i} x1={i * 160} y1="0" x2={i * 160 + 80} y2="400" stroke="#C0C5CE" strokeWidth="1" strokeDasharray="6,14" />
            ))}
          </svg>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <p className="text-xs font-sans text-silver/50 tracking-[0.2em] uppercase mb-4">{t('tradeDataPage.eyebrow')}</p>
          <h1 className="font-serif text-white text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight mb-4 lg:mb-5 max-w-2xl">
            {t('tradeDataPage.heading1')}<br />
            <span className="italic font-light text-silver">{t('tradeDataPage.heading2')}</span>
          </h1>
          <p className="text-white/60 font-sans text-base lg:text-lg max-w-xl leading-relaxed">
            {t('tradeDataPage.desc')}
          </p>
        </div>
      </section>

      {/* Key Metrics */}
      <section ref={ref} className="bg-[#F8F9FB] py-10 lg:py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {tradeStats.map((s, i) => (
              <div key={s.labelKey} className={`reveal stagger-${i + 1} bg-white rounded-xl p-5 lg:p-6 border border-silver-light`}>
                <p className="text-navy/50 text-xs font-sans font-medium tracking-wide mb-2 lg:mb-3">{t(s.labelKey)}</p>
                <p className="font-serif text-navy text-2xl lg:text-3xl font-bold mb-2">{s.value}</p>
                <div className="flex items-center gap-1">
                  <i className={`${s.up ? 'ri-arrow-up-line text-emerald-500' : 'ri-arrow-down-line text-red-400'} text-sm`} />
                  <span className={`text-sm font-sans font-medium ${s.up ? 'text-emerald-500' : 'text-red-400'}`}>{s.change}</span>
                  <span className="text-navy/30 text-xs font-sans ml-1">{t('tradeDataPage.yoy')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Charts */}
      <section className="bg-[#F8F9FB] pb-16 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6">
          {/* Monthly Trade Volume */}
          <div className="reveal bg-white rounded-2xl p-5 lg:p-8 border border-silver-light mb-5 lg:mb-6">
            <h2 className="font-serif text-navy text-lg lg:text-2xl font-bold mb-1">{t('tradeDataPage.monthly.title')}</h2>
            <p className="text-navy/50 text-sm font-sans mb-4 lg:mb-6">{t('tradeDataPage.monthly.sub')}</p>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={monthlyTradeVolume} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="koreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0A1628" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#0A1628" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="vnGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C0C5CE" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#C0C5CE" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8EAED" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: 'Inter', fill: '#9BA3AF' }} />
                <YAxis tick={{ fontSize: 10, fontFamily: 'Inter', fill: '#9BA3AF' }} width={40} />
                <Tooltip
                  contentStyle={{ fontFamily: 'Inter', fontSize: 12, borderRadius: 8, border: '1px solid #E8EAED' }}
                  formatter={(v: number) => [`$${v}M`, '']}
                />
                <Legend wrapperStyle={{ fontFamily: 'Inter', fontSize: 12 }} />
                <Area type="monotone" dataKey="korea" name={t('tradeDataPage.korea.exports')} stroke="#0A1628" strokeWidth={2} fill="url(#koreaGrad)" />
                <Area type="monotone" dataKey="vietnam" name={t('tradeDataPage.vietnam.exports')} stroke="#C0C5CE" strokeWidth={2} fill="url(#vnGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
            {/* Trade by Category */}
            <div className="reveal bg-white rounded-2xl p-5 lg:p-8 border border-silver-light">
              <h2 className="font-serif text-navy text-lg lg:text-xl font-bold mb-1">{t('tradeDataPage.category.title')}</h2>
              <p className="text-navy/50 text-sm font-sans mb-4 lg:mb-6">{t('tradeDataPage.category.sub')}</p>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={tradeCategories} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8EAED" />
                  <XAxis dataKey="category" tick={{ fontSize: 10, fontFamily: 'Inter', fill: '#9BA3AF' }} />
                  <YAxis tick={{ fontSize: 10, fontFamily: 'Inter', fill: '#9BA3AF' }} width={35} />
                  <Tooltip contentStyle={{ fontFamily: 'Inter', fontSize: 12, borderRadius: 8 }} formatter={(v: number) => [`${v}%`, '']} />
                  <Legend wrapperStyle={{ fontFamily: 'Inter', fontSize: 11 }} />
                  <Bar dataKey="korea" name={t('tradeDataPage.korea.label')} fill="#0A1628" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="vietnam" name={t('tradeDataPage.vietnam.label')} fill="#C0C5CE" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Trade Distribution Pie */}
            <div className="reveal bg-white rounded-2xl p-5 lg:p-8 border border-silver-light">
              <h2 className="font-serif text-navy text-lg lg:text-xl font-bold mb-1">{t('tradeDataPage.distribution.title')}</h2>
              <p className="text-navy/50 text-sm font-sans mb-4 lg:mb-6">{t('tradeDataPage.distribution.sub')}</p>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={tradeDistribution}
                    cx="50%"
                    cy="50%"
                    outerRadius={85}
                    dataKey="value"
                    label={({ name, value }) => `${name} ${value}%`}
                    labelLine={false}
                  >
                    {tradeDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontFamily: 'Inter', fontSize: 12, borderRadius: 8 }} formatter={(v: number) => [`${v}%`, '']} />
                  <Legend wrapperStyle={{ fontFamily: 'Inter', fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
